import { Navigate, useLocation } from "react-router-dom"
import { useEffect, useState } from "react"

import { useAppKitAccount } from "@reown/appkit/react"
import { useAuth } from "@/contexts/auth-context"
import { useConnection } from "wagmi"
import { usePersistentAuthRedirect } from "@/hooks/auth"

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isConnected: isAppKitConnected, address: appKitAddress } =
    useAppKitAccount()
  const {
    isConnected,
    isReconnecting,
    address: connectedAddress,
  } = useConnection()
  const { isVerified, isAuthLoading, address } = useAuth()
  const { markLastAuthAddress, saveRedirectContext } =
    usePersistentAuthRedirect()
  const location = useLocation()
  const [hasWaited, setHasWaited] = useState(false)

  const currentAddress =
    (connectedAddress ?? appKitAddress ?? address)?.toLowerCase() ?? null

  // Wait for connection to restore on initial load
  useEffect(() => {
    // If reconnecting, wait for it to complete
    if (isReconnecting) {
      return
    }

    // If already connected, no need to wait
    if (isConnected || isAppKitConnected) {
      setHasWaited(true)
      return
    }

    // Wait a short time (500ms) for connection to restore, then allow redirect
    const timer = setTimeout(() => {
      setHasWaited(true)
    }, 500)

    return () => clearTimeout(timer)
  }, [isReconnecting, isConnected, isAppKitConnected])

  useEffect(() => {
    if (isVerified && currentAddress) {
      markLastAuthAddress(currentAddress)
    }
  }, [isVerified, currentAddress, markLastAuthAddress])

  // Don't redirect while waiting for connection to restore or auth loading
  if (isReconnecting || !hasWaited || isAuthLoading) {
    return null // Render nothing while waiting to avoid flash
  }

  // Redirect to connect wallet page if not connected or not verified
  if (!isConnected && !isAppKitConnected) {
    saveRedirectContext({
      from: {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      previousAddress: currentAddress,
    })
    return <Navigate to="/auth" replace />
  }

  // Redirect to auth page if connected but not verified
  if ((isConnected || isAppKitConnected) && !isVerified) {
    saveRedirectContext({
      from: {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      previousAddress: currentAddress,
    })
    return <Navigate to="/auth" replace />
  }

  return <>{children}</>
}
